import {
  createFileRoute,
  notFound,
  Outlet,
  LinkComponentProps,
  useSearch,
} from "@tanstack/react-router";
import { APIError } from "web-core/utils";

import { ClientInfo } from "@crm/components/client-info";
import {
  PageNavbar,
  PageNavbarItem,
} from "@core/components/layout/page-navbar";
import {
  clientDetailsQueryOptions,
  investorProfileQueryOptions,
  useInvestorProfile,
  usePortfolioSummaryV2,
} from "creso-core/hooks";
import { ClientStatusComponent } from "@crm/components/status/client-status";
import { ImportHoldingsDialog } from "@crm/components/portfolio/import-holdings/dialog";
import { CapitalGainsDialog } from "@crm/components/portfolio/reports/capital-gains-dialog";
import { isActiveClient } from "creso-core/utils";
import { SipDetails } from "@crm/components/portfolio/sip-details";
import { StpDetails } from "@crm/components/portfolio/stp-details";
import { PortfolioValuation } from "@crm/components/portfolio/reports/portfolio-valuation";
import { DownloadOrderHistory } from "@crm/components/portfolio/reports/download-order-history";
import { boolean, number, object, string, z } from "zod";
import { portfolioSourceSchema } from "creso-core/zod";
import { Route as RootRoute } from "@/routes/__root";
import { NotifyUpdates } from "@crm/components/notify-updates";
import { SwpDetails } from "@crm/components/portfolio/swp-details";
import { Page } from "@core/components/layout/page";
import { useMemo } from "react";
import { RiskProfilerDialog } from "@crm/components/risk-profile/risk-profile";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { useUnstableFeatures } from "@core/hooks/use-unstable-features";
import { Route as AccountRoute } from "./$id/account";
import { Route as PortfolioRoute } from "./$id/portfolio";
import { Route as PlanningRoute } from "./$id/planning";
import { Route as ReviewRoute } from "./$id/review";

function ClientLayout() {
  const { client } = Route.useLoaderData();

  useInvestorProfile(
    { client_id: client.id },
    {
      enabled: Boolean(
        client.holding_pattern &&
        JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
      ),
    }
  );

  const activeClient = isActiveClient(client);

  const unstableFeatures = useUnstableFeatures();

  const setupPendingClient = client.status === "SETUP_PENDING";

  const { data: summary } = usePortfolioSummaryV2(
    client.investment_account ?? ""
  );

  const hasAum = useMemo(() => {
    const overall = summary?.find((s) => s.source_type === "OVERALL");
    return Number(overall?.current_value) > 0;
  }, [summary]);

  const navItems: (LinkComponentProps & { label: string; show: boolean })[] = [
    {
      to: "/clients/$id",
      params: { id: client.id.toString() },
      activeOptions: { exact: true, includeSearch: false },
      label: "Overview",
      show: activeClient || hasAum,
    },
    {
      to: PortfolioRoute.fullPath,
      params: { id: client.id.toString() },
      label: "Mutual Funds",
      show: activeClient || setupPendingClient,
      activeOptions: { exact: true, includeSearch: false },
    },
    {
      to: ReviewRoute.fullPath,
      params: { id: client.id.toString() },
      label: "Review",
      show: (activeClient || hasAum) && unstableFeatures.portfolioReview,
      activeOptions: { exact: true, includeSearch: false },
    },
    {
      to: PlanningRoute.fullPath,
      params: { id: client.id.toString() },
      label: "Planning",
      show: true,
      activeOptions: { exact: true, includeSearch: false },
    },
    {
      to: AccountRoute.fullPath,
      params: { id: client.id.toString() },
      label: "Account center",
      show: activeClient,
      activeOptions: { exact: true, includeSearch: false },
    },
  ];

  const visibleNavItems = navItems.filter((f) => f.show);

  return (
    <Page className="lg:flex-row size-full gap-2">
      <Page.Sidebar className="px-4 py-5 overflow-auto">
        <ClientInfo />
      </Page.Sidebar>
      <Page.StatusBar className="[&>div]:w-full">
        <ClientStatusComponent />
      </Page.StatusBar>
      <Page.Content className="h-full bg-background">
        {visibleNavItems.length > 1 && (
          <PageNavbar className="sticky top-0 left-0 right-0 z-40 bg-background">
            {visibleNavItems.map((n) => (
              <PageNavbarItem
                key={n.to}
                to={n.to}
                params={n.params}
                activeOptions={n.activeOptions}
              >
                {n.label}
              </PageNavbarItem>
            ))}
          </PageNavbar>
        )}
        <Outlet />
      </Page.Content>
    </Page>
  );
}

export const Route = createFileRoute("/(auth)/_headed/clients/$id")({
  validateSearch: object({
    detail: z.enum(["sip", "swp", "stp", "orders", "holdings"]).optional(),
    retry: boolean().optional(),
    id: string().or(number()).optional(),
    source: portfolioSourceSchema.optional(),
  }),
  component: function Component() {
    const { client } = Route.useLoaderData();
    const { modal } = useSearch({
      from: RootRoute.id,
      select: (search) => ({ modal: search?.modal }),
    });
    return (
      <>
        <ClientLayout />
        {modal === "risk-profile" ? (
          <RiskProfilerDialog />
        ) : modal === "capital-gains" ? (
          <CapitalGainsDialog client={client} />
        ) : modal === "import-holdings" ? (
          <ImportHoldingsDialog />
        ) : modal === "sip-details" ? (
          <SipDetails />
        ) : modal === "stp-details" ? (
          <StpDetails />
        ) : modal === "swp-details" ? (
          <SwpDetails />
        ) : modal === "portfolio-valuation" ? (
          <PortfolioValuation client={client} />
        ) : modal === "account-updates" ? (
          <NotifyUpdates />
        ) : modal === "order-history" ? (
          <DownloadOrderHistory client={client} />
        ) : null}
      </>
    );
  },
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const client = await queryClient
      .fetchQuery(clientDetailsQueryOptions(Number(params.id)))
      .catch((error) => {
        // Show the not-found page instead of reporting a missing client to Sentry
        if (error instanceof APIError && error.status === 404) throw notFound();
        throw error;
      });
    if (
      client.holding_pattern &&
      JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
    ) {
      await queryClient.ensureQueryData(
        investorProfileQueryOptions({ client_id: client.id })
      );
    }
    return { client };
  },
});
